import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import type { Id } from '@contracts/common';
import type {
  BoardResponse, DonePage, ItemCreateBody, ItemDetailDTO, ItemExternalLink, ItemFinanceLinkDTO, ItemFinanceLinkListDTO,
  ItemMoveBody, ItemPatchBody, ItemRecord, ItemTreeDTO,
} from '@contracts/workplace';
import { api, type ApiClientError } from '../../api/client';
import type { UndoCommand } from './model';

/**
 * The Workplace endpoints (spec §5.2, §5.3). Each write carries the request id of its save so a replay after a
 * lost answer is recognised by the server; nothing here retries.
 */

const ITEMS = '/api/workplace/items';

/** A read's query string, without the parameters left undefined. */
function query(entries: [string, string | undefined][]): string {
  const params = new URLSearchParams();
  for (const [key, value] of entries) if (value !== undefined) params.set(key, value);
  const text = params.toString();
  return text === '' ? '' : `?${text}`;
}

/**
 * The viewer's one board — every root whose home they see (sharing §3) — or `{unchanged}` when `since` (the revision
 * and reader tag of the board the cache holds) is still current for this reader. The server refuses the gone `?space=`.
 */
export const fetchBoard = (since?: { revision: number; reader: string }): Promise<BoardResponse> =>
  api<BoardResponse>(`/api/workplace/board${query([
    ['since', since?.revision.toString()], ['reader', since?.reader],
  ])}`);

export const fetchDone = (cursor?: string): Promise<DonePage> =>
  api<DonePage>(`/api/workplace/done${query([['cursor', cursor]])}`);

export const fetchTree = (id: Id): Promise<ItemTreeDTO> => api<ItemTreeDTO>(`${ITEMS}/${id}/tree`);

export const fetchItem = (id: Id): Promise<ItemDetailDTO> => api<ItemDetailDTO>(`${ITEMS}/${id}`);

export const createItem = (body: ItemCreateBody, requestId: Id): Promise<ItemRecord> =>
  api<ItemRecord>(ITEMS, { method: 'POST', body, requestId });

export const patchItem = (id: Id, body: ItemPatchBody, requestId: Id): Promise<ItemRecord> =>
  api<ItemRecord>(`${ITEMS}/${id}`, { method: 'PATCH', body, requestId });

/** A move carries no version: last writer wins (spec §5.3). */
export const moveItem = (id: Id, body: ItemMoveBody, requestId: Id): Promise<ItemRecord> =>
  api<ItemRecord>(`${ITEMS}/${id}/move`, { method: 'POST', body, requestId });

export const deleteItem = (id: Id, version: number, requestId: Id): Promise<unknown> =>
  api<unknown>(`${ITEMS}/${id}`, { method: 'DELETE', body: { version }, requestId });

/**
 * Sends one undo command (spec §6.7) as the ordinary request it stands for. A recreated root's body already names its
 * home (`sharing.revive` or `projectId`, sharing D6), so it is sent as built.
 */
export function sendUndo(command: UndoCommand, requestId: Id): Promise<unknown> {
  switch (command.kind) {
    case 'create': return createItem(command.body, requestId);
    case 'delete': return deleteItem(command.id, command.version, requestId);
    case 'move': return moveItem(command.id, command.body, requestId);
    case 'patch': return patchItem(command.id, command.body, requestId);
  }
}

/** A card's finance links as its reader may see them (S5 D157). Under `['workplace']`, so a board refresh reads it too. */
export const financeLinksKey = (itemId: Id) => ['workplace', 'finance-links', itemId] as const;

const financeLinksPath = (itemId: Id) => `${ITEMS}/${encodeURIComponent(itemId)}/finance-links`;

export function useFinanceLinks(itemId: Id): UseQueryResult<ItemFinanceLinkListDTO, ApiClientError> {
  return useQuery<ItemFinanceLinkListDTO, ApiClientError>({
    queryKey: financeLinksKey(itemId),
    queryFn: () => api<ItemFinanceLinkListDTO>(financeLinksPath(itemId)),
    retry: false,
  });
}

/**
 * "Liên kết" / "Bỏ liên kết" (S5 §10.2): each press is one request with its own `Idempotency-Key` (D41). Every
 * `['workplace']` read is refreshed after it: the board (its chip, D154), the card's links and the work lists.
 */
export function useLinkFinance(itemId: Id): UseMutationResult<ItemFinanceLinkDTO, ApiClientError, Id> {
  const queryClient = useQueryClient();
  return useMutation<ItemFinanceLinkDTO, ApiClientError, Id>({
    mutationFn: (financeEntryId) => api<ItemFinanceLinkDTO>(financeLinksPath(itemId),
      { method: 'POST', body: { financeEntryId }, requestId: crypto.randomUUID() }),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['workplace'] }); },
  });
}

/** The empty body is load-bearing: the API answers 422 to a JSON-typed request without one (sharing/api.ts). */
export function useUnlinkFinance(itemId: Id): UseMutationResult<ItemFinanceLinkDTO, ApiClientError, Id> {
  const queryClient = useQueryClient();
  return useMutation<ItemFinanceLinkDTO, ApiClientError, Id>({
    mutationFn: (linkId) => api<ItemFinanceLinkDTO>(`${financeLinksPath(itemId)}/${encodeURIComponent(linkId)}`,
      { method: 'DELETE', body: {}, requestId: crypto.randomUUID() }),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['workplace'] }); },
  });
}

/**
 * "Gỡ" on an outside-source chip (S6 §8.2): one DELETE with its own `Idempotency-Key` (the route answers through
 * `mutate`, 404 once the chip is gone). Every `['workplace']` read and the card's detail are refreshed after it,
 * refused or not, so a chip removed meanwhile by someone else (or by Hana) leaves the screen too.
 */
export function useUnlinkExternal(itemId: Id): UseMutationResult<ItemExternalLink, ApiClientError, Id> {
  const queryClient = useQueryClient();
  return useMutation<ItemExternalLink, ApiClientError, Id>({
    mutationFn: (linkId) => api<ItemExternalLink>(
      `${ITEMS}/${encodeURIComponent(itemId)}/external-links/${encodeURIComponent(linkId)}`,
      { method: 'DELETE', body: {}, requestId: crypto.randomUUID() }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['workplace'] });
      void queryClient.invalidateQueries({ queryKey: ['task', itemId] });
    },
  });
}
